import bbox from '@turf/bbox'
import {
  BBox,
  FeatureCollection,
  featureCollection,
  point,
} from '@turf/helpers'
import { GeoJsonLayer, WebMercatorViewport } from 'deck.gl'
import { Position } from 'geojson'
import { isEqual, min } from 'lodash'
import { Component, useEffect, useMemo, useState } from 'react'

import { QueryResult } from '@apollo/client'
import Map, { BasisMap, ViewState } from '~/components/Map'
import {
  ServiceAreaGeoJsonLayer,
  VehicleIconLayer,
  ZoneIconLayers,
} from '~/components/Map/layers'
import Legend, { LegendProps } from '~/components/Map/Legend'
import Tooltip from '~/components/Map/Tooltip'
import withSchemeServiceAreas, {
  SERVICE_AREAS_GEOJSON,
} from '~/components/Scheme/withSchemeServiceAreas'
import {
  JourneyMapFragment,
  JourneyMapQuery_all_zones_ZoneConnection,
  JourneyMapQuery_Query,
  JourneyMapQuery_vehicles_VehicleConnection,
  JourneySetMapQuery_Query,
  useJourneyMapLazyQuery,
  useJourneySetMapLazyQuery,
} from '~/gql/generated/graphql'
import useContainerSize from '~/hooks/useContainerSize'
import { addTimesToJourneyRoute } from '~/utils'
import { LoadingComponent } from '../Query/utils'

const DEFAULT_ZOOM = 15

type Size = {
  width: number
  height: number
}

let viewport = new WebMercatorViewport(BasisMap.defaultProps)

type MapData = {
  journeys: JourneyMapFragment[]
  vehicles?: JourneyMapQuery_vehicles_VehicleConnection
  all_zones?: JourneyMapQuery_all_zones_ZoneConnection
}

type MapProps = {
  data: MapData
  location: {
    latitude: number
    longitude: number
    zoom: number
  }
  serviceAreas: SERVICE_AREAS_GEOJSON
  onViewStateChange: (viewState: ViewState) => void
  activeJourneyId?: string
}

type MapState = {
  x: number | null
  y: number | null
  hoveredObject: { properties: unknown } | null
  bounds: Position[] | null
  legendProps: LegendProps
}

class JourneyMap extends Component<MapProps, MapState> {
  state: MapState = {
    x: null,
    y: null,
    hoveredObject: null,
    bounds: null,
    legendProps: { type: null },
  }

  _setLegend = (legendProps: MapState['legendProps']) => {
    this.setState({
      legendProps,
    })
  }

  _setToolTip = ({ object, x, y }) =>
    this.setState({ x, y, hoveredObject: object })

  //skipcq JS-0105
  _getLineWidth = ({ properties, isActiveRoute }) => {
    const multiplier = isActiveRoute ? 2 : 1
    switch (properties.name) {
      case 'Raw':
      case 'Fast':
        return 3 * multiplier
      case 'start':
      case 'end':
      case 'final':
        return 6 * multiplier
      default:
        return 0
    }
  }

  //skipcq JS-0105
  _getFillColor = ({ properties }) => {
    switch (properties.name) {
      case 'start':
        return [56, 231, 100] // green for go
      case 'end':
        return [231, 56, 100] // red for stop
      case 'final':
        return [243, 163, 26] // amber for final
      case 'step':
        return [82, 82, 92] // points in between start & end
      case 'Fast':
        return [255, 164, 14] // in progress line
      default:
        return [67, 225, 183]
    }
  }

  _generateRouteLayer = (
    id: string,
    route: FeatureCollection,
    isActiveRoute: boolean,
  ) => {
    const routeWithTimes = addTimesToJourneyRoute(route)

    return new GeoJsonLayer({
      id: `geoJSON-layer-${id}`,
      data: routeWithTimes,
      opacity: isActiveRoute ? 0.9 : 0.1,
      stroked: true,
      filled: true,
      lineWidthMinPixels: 2,
      getRadius: () => 3 * (isActiveRoute ? 2 : 1),
      getLineWidth: props => this._getLineWidth({ ...props, isActiveRoute }),
      lineJointRounded: true,
      getLineColor: this._getFillColor,
      getFillColor: this._getFillColor,
      pickable: true,
      autoHighlight: true,
      onHover: args => this._setToolTip(args),
    })
  }

  render() {
    const { serviceAreas, data, location } = this.props
    const { x, y, hoveredObject, legendProps } = this.state

    const mapProps: {
      layers: GeoJsonLayer[]
      latitude: number
      longitude: number
      zoom: number
    } = {
      layers: [],
      latitude: location?.latitude,
      longitude: location?.longitude,
      zoom: location?.zoom,
    }

    mapProps.layers = [
      ServiceAreaGeoJsonLayer({
        serviceAreas,
        layerId: 'service-area-layer',
        layerProps: {
          onClick: ({ object: { properties } }) =>
            this._setLegend({
              type: 'ServiceArea',
              service_area: properties,
            }),
        },
      }),
      VehicleIconLayer({
        // Always filter out the journey module because we want to make it bigger than the
        // other modules.
        vehicles:
          data?.vehicles?.nodes.filter(
            v =>
              !data?.journeys.map(({ vehicle }) => vehicle?.id).includes(v?.id),
          ) ?? [],
        layerId: 'vehicles-icon-layer',
        onHover: this._setToolTip,
        layerProps: {
          getSize: 33,
          onClick: ({ object }) =>
            this._setLegend({
              type: 'Vehicle',
              module_id: object?.detail?.module?.id,
            }),
        },
      }),
      ...ZoneIconLayers({
        zones: data?.all_zones?.nodes ?? [],
        layerId: 'zone-layer',
        onHover: this._setToolTip,
        iconLayerProps: {
          onClick: ({ object }) =>
            this._setLegend({
              type: 'Zone',
              zone_id: object.id,
            }),
        },
      }),
    ]

    data.journeys.forEach(journey => {
      const isActiveRoute = journey.id === this.props.activeJourneyId
      if (!!journey?.route?.features?.length) {
        const routeLayer = this._generateRouteLayer(
          journey.id,
          journey.route,
          isActiveRoute,
        )
        mapProps.layers.push(routeLayer)
      }
      if (journey?.vehicle?.id) {
        mapProps.layers.push(
          VehicleIconLayer({
            vehicles: journey?.vehicle ? [journey.vehicle] : [],
            layerId: `vehicle-icon-layer-${journey.vehicle.id}`,
            layerProps: {
              opacity: isActiveRoute ? 1 : 0.5,
              onClick: ({ object }) =>
                this._setLegend({
                  type: 'Vehicle',
                  module_id: object?.detail?.module?.id,
                }),
            },
          }),
        )
      }
    })

    return (
      <Map
        {...mapProps}
        message="No route data is available for this journey"
        onViewStateChange={this.props.onViewStateChange}
      >
        {hoveredObject && (
          // @ts-expect-error vite upgrade
          <Tooltip x={x} y={y} properties={hoveredObject.properties} />
        )}
        <Legend
          onClose={() => this._setLegend({ type: null })}
          {...legendProps}
        />
      </Map>
    )
  }
}

const JourneyMapWithServiceAreas = withSchemeServiceAreas(JourneyMap)

function getLocation(
  [neLng, neLat, swLng, swLat]: BBox,
  size?: Size,
  padding?: number,
) {
  const bounds = [
    [neLng, neLat],
    [swLng, swLat],
  ]
  try {
    viewport = viewport.fitBounds(bounds, {
      padding: padding ?? 50,
      width: size?.width,
      height: size?.height,
    })

    return {
      latitude: viewport.latitude,
      longitude: viewport.longitude,
      zoom: min([viewport.zoom, DEFAULT_ZOOM]),
    }
  } catch (err) {
    // tslint:disable-next-line:no-console
    console.log(err)
    // tslint:disable-next-line:no-console
    console.log("Couldn't calculate map bounds")
    return undefined
  }
}

function round(n: number) {
  return n.toFixed(5)
}

type MapContainerProps = {
  schemeId: string
  activeJourneyId?: string
  pollInterval?: number
} & (
  | { journey_id: string; journey_set_id?: never }
  | { journey_id?: never; journey_set_id: string }
)

const getMapLocationFromJourneys = (
  journeys?: JourneyMapFragment[],
  activeJourneyId?: string,
  size?: Size,
) => {
  if (!journeys || journeys.length === 0) {
    return null
  }
  if (activeJourneyId) {
    const activeJourney = journeys.find(({ id }) => id === activeJourneyId)
    if (activeJourney) {
      // Try to focus on the route
      const location = getLocation(bbox(activeJourney.route), size)
      if (location) return location
      // If that fails, try to focus on the module
      if (activeJourney.vehicle.detail.module?.location) {
        const { latitude, longitude } =
          activeJourney.vehicle.detail.module.location
        return {
          latitude,
          longitude,
          zoom: DEFAULT_ZOOM,
        }
      }
    }
  } else {
    // Try to focus on all routes
    const routeFeatures = journeys
      ?.map(({ route }) => route)
      .flatMap(route => route?.features ?? [])
    let location = getLocation(bbox(featureCollection(routeFeatures)), size)
    if (location) return location
    // If that fails, try to focus on all modules
    const modulesLocations = journeys
      .map(j => j.vehicle.detail.module?.location)
      .filter(l => !!l)
      .map(({ latitude, longitude }) => point([longitude, latitude]))
    location = getLocation(bbox(featureCollection(modulesLocations)), size)
    if (location) return location
  }
  return null
}

const MapContainer = ({
  journey_id,
  journey_set_id,
  schemeId,
  activeJourneyId,
  pollInterval,
}: MapContainerProps) => {
  const [bounds, setBounds] = useState<BBox>()
  const hasBounds = bounds && bounds?.length > 0

  const [data, setData] = useState<MapData | null>(null)
  const [hasInteracted, setHasInteracted] = useState(false)

  const [journeyMapQuery, journeyMapResult] = useJourneyMapLazyQuery({
    pollInterval,
  })
  const [journeySetMapQuery, journeySetMapResult] = useJourneySetMapLazyQuery({
    pollInterval,
  })

  const { size, containerRef } = useContainerSize()

  useEffect(() => {
    const getProps = (id: string) => ({
      pollInterval: pollInterval ?? 0,
      variables: {
        id,
        hasLocation: !!hasBounds,
        location: hasBounds
          ? {
              longitude_min: bounds[0],
              latitude_min: bounds[1],
              longitude_max: bounds[2],
              latitude_max: bounds[3],
            }
          : null,
        schemeId,
      },
    })

    if (journey_id) {
      journeyMapQuery(getProps(journey_id))
    }
    if (journey_set_id) {
      journeySetMapQuery(getProps(journey_set_id))
    }
  }, [pollInterval, hasBounds, bounds])

  const populateData = (
    result:
      | QueryResult<JourneyMapQuery_Query>
      | QueryResult<JourneySetMapQuery_Query>,
  ) => {
    const { data: currentData, loading, previousData } = result
    const data = currentData ?? (loading ? previousData : null)

    if (!data) {
      return
    }

    const newData = { vehicles: data.vehicles, all_zones: data.all_zones }

    if ('journey' in data && data.journey) {
      setData({ ...newData, journeys: [data.journey] })
    } else if ('journey_set' in data) {
      setData({ ...newData, journeys: data.journey_set.journeys })
    }
  }

  useEffect(() => populateData(journeyMapResult), [journeyMapResult])

  useEffect(() => populateData(journeySetMapResult), [journeySetMapResult])

  const setBoundsIfNew = (newBounds: BBox) => {
    if (!hasBounds) {
      setBounds(newBounds)
      return
    }

    if (!isEqual(newBounds.map(round), bounds.map(round))) {
      setBounds(newBounds)
    }
  }

  const onViewStateChange = ({ bounds }: ViewState) => {
    const { _ne, _sw } = bounds
    setBoundsIfNew([_ne.lng, _ne.lat, _sw.lng, _sw.lat])
  }

  const rawLocation = useMemo(
    () =>
      getMapLocationFromJourneys(data?.journeys, activeJourneyId, size) ??
      (bounds ? getLocation(bounds, size) : null),
    [data, size, activeJourneyId, bounds],
  )

  const [location, setLocation] = useState(rawLocation)
  const onInteraction = () => setHasInteracted(true)

  useEffect(() => {
    // If the user has manually interacted with the map, don't automatically
    // update location and zoom
    if (!hasInteracted) {
      setLocation(rawLocation)
    }
  }, [rawLocation, hasInteracted])

  return (
    <div
      className="minimap-wrapper"
      ref={containerRef}
      onClick={onInteraction}
      onWheel={onInteraction}
    >
      {!data ? (
        <LoadingComponent />
      ) : (
        <JourneyMapWithServiceAreas
          data={data}
          activeJourneyId={activeJourneyId}
          location={location}
          onViewStateChange={onViewStateChange}
        />
      )}
    </div>
  )
}

export default MapContainer
